import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGet, type Game, type Listing } from '../lib/api';
import ListingCard from '../components/ListingCard';
import ListingFilters, { type FilterState } from '../components/ListingFilters';
import { demoGames, demoListingsForBrowse, useDemoMode } from '../lib/demo';

const PAGE_SIZE = 24;

export default function ListPage() {
  const [demoOn] = useDemoMode();
  const [params] = useSearchParams();
  const [games, setGames] = useState<Game[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  // Seed filters from ?game_id= / ?section= (home-page search + game cards).
  const [filters, setFilters] = useState<FilterState>({
    game_id: params.get('game_id') ?? '',
    section_types: [],
    section: params.get('section') ?? '',
    min_price: '',
    max_price: '',
    min_qty: '',
    sort: 'price_asc',
  });

  useEffect(() => {
    apiGet<{ games: Game[] }>('/games')
      .catch((e) => {
        if (!demoOn) setError(e instanceof Error ? e.message : 'Could not load games.');
        return { games: [] as Game[] };
      })
      .then(({ games }) => {
        const list = games.length ? games : demoOn ? demoGames() : [];
        setGames(list);
        setFilters((f) =>
          f.game_id || !list.length ? f : { ...f, game_id: list[0]!.game_id }
        );
      });
  }, [demoOn]);

  // Client-side sample listings, appended after real inventory while testing.
  const demoListings = useMemo(
    () => (demoOn ? demoListingsForBrowse(filters, games) : []),
    [demoOn, filters, games]
  );

  function buildQuery(offset: number): string {
    const q = new URLSearchParams();
    if (filters.game_id) q.set('game_id', filters.game_id);
    if (filters.section_types.length) q.set('section_type', filters.section_types.join(','));
    if (filters.section) q.set('section', filters.section);
    if (filters.min_price) q.set('min_price', String(Number(filters.min_price) * 100));
    if (filters.max_price) q.set('max_price', String(Number(filters.max_price) * 100));
    if (filters.min_qty) q.set('min_qty', filters.min_qty);
    q.set('sort', filters.sort);
    q.set('limit', String(PAGE_SIZE));
    q.set('offset', String(offset));
    return q.toString();
  }

  // Reset + load page 1 whenever filters change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGet<{ listings: Listing[] }>(`/listings?${buildQuery(0)}`)
      .then(({ listings }) => {
        if (cancelled) return;
        setListings(listings);
        setHasMore(listings.length === PAGE_SIZE);
      })
      .catch((e) => {
        // With demo data on, sample listings still render — don't show a raw error.
        if (!cancelled && !demoOn) {
          setError(e instanceof Error ? e.message : 'Could not load listings.');
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters, demoOn]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const { listings: next } = await apiGet<{ listings: Listing[] }>(
        `/listings?${buildQuery(listings.length)}`
      );
      setListings((cur) => [...cur, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load more listings.');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section>
      <h2>Available tickets</h2>
      <ListingFilters games={games} value={filters} onChange={setFilters} />

      {error && <p style={{ color: 'crimson' }}>{error}</p>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && !error && listings.length === 0 && demoListings.length === 0 && (
        <p className="muted">No listings match those filters.</p>
      )}
      {demoOn && demoListings.length > 0 && (
        <p className="muted">
          Includes sample listings (badged "SAMPLE") — turn off "Demo data" in the top bar to
          hide them.
        </p>
      )}

      <div className="flex flex-col gap-3 mt-4">
        {listings.map((l) => (
          <ListingCard key={l.listing_id} listing={l} />
        ))}
        {!loading &&
          demoListings.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
      </div>

      {hasMore && !loading && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button className="btn-secondary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}
